import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getActiveContractKinds } from 'api/contract.api';
import { getAll } from 'api/devices.api';
import {
  getEmployeesWithSchedulesOfToday,
  getUpcomingBirthdays,
  getUpcomingSeniorityAnniversaries,
} from 'api/user.api';
import { SliceStatus } from 'constants/enums/slices';
import { ContractKind } from 'types/contract';
import { Device } from 'types/devices';
import { EmployeeWithScheduleOfToday, UpcomingEmployeeEvent } from 'types/user';

interface DashboardSlice {
  employeesList: EmployeeWithScheduleOfToday[];
  employeesListStatus: SliceStatus;
  contractKindsCount: ContractKind[];
  contractKindsCountSliceStatus: SliceStatus;
  devicesList: Device[];
  devicesListSliceStatus: SliceStatus;
  upcomingBirthdays: UpcomingEmployeeEvent[];
  upcomingBirthdaysStatus: SliceStatus;
  upcomingSeniorityAnniversaries: UpcomingEmployeeEvent[];
  upcomingSeniorityAnniversariesStatus: SliceStatus;
}

const initialState: DashboardSlice = {
  employeesList: [],
  employeesListStatus: SliceStatus.Empty,
  contractKindsCount: [],
  contractKindsCountSliceStatus: SliceStatus.Empty,
  devicesList: [],
  devicesListSliceStatus: SliceStatus.Empty,
  upcomingBirthdays: [],
  upcomingBirthdaysStatus: SliceStatus.Empty,
  upcomingSeniorityAnniversaries: [],
  upcomingSeniorityAnniversariesStatus: SliceStatus.Empty,
};

export const doGetDashboardContractKindsCount = createAsyncThunk(
  'dashboard/contractKindsCount',
  async (_, { rejectWithValue }) => {
    try {
      return await getActiveContractKinds();
    } catch (error) {
      return await rejectWithValue(error);
    }
  }
);

export const doGetDashboardEmployeesList = createAsyncThunk(
  'dashboard/employeesList',
  async (_, { rejectWithValue }) => {
    try {
      return await getEmployeesWithSchedulesOfToday();
    } catch (error) {
      return await rejectWithValue(error);
    }
  }
);

export const doGetDashboardDevicesList = createAsyncThunk(
  'dashboard/devicesList',
  async (_, { rejectWithValue }) => {
    try {
      return await getAll();
    } catch (error) {
      return await rejectWithValue(error);
    }
  }
);

export const doGetDashboardUpcomingBirthdays = createAsyncThunk(
  'dashboard/upcomingBirthdays',
  async (_, { rejectWithValue }) => {
    try {
      return await getUpcomingBirthdays();
    } catch (error) {
      return await rejectWithValue(error);
    }
  }
);

export const doGetDashboardUpcomingSeniorityAnniversaries = createAsyncThunk(
  'dashboard/upcomingSeniorityAnniversaries',
  async (_, { rejectWithValue }) => {
    try {
      return await getUpcomingSeniorityAnniversaries();
    } catch (error) {
      return await rejectWithValue(error);
    }
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(doGetDashboardContractKindsCount.pending, (state) => {
      state.contractKindsCountSliceStatus = SliceStatus.Updating;
    });
    builder.addCase(
      doGetDashboardContractKindsCount.fulfilled,
      (state, action) => {
        if (action.payload.data) {
          state.contractKindsCount = action.payload.data;
        }
        state.contractKindsCountSliceStatus = SliceStatus.Fulfilled;
      }
    );
    builder.addCase(doGetDashboardContractKindsCount.rejected, (state) => {
      state.contractKindsCountSliceStatus = SliceStatus.Error;
    });
    builder.addCase(doGetDashboardEmployeesList.pending, (state) => {
      state.employeesListStatus = SliceStatus.Updating;
    });
    builder.addCase(doGetDashboardEmployeesList.fulfilled, (state, action) => {
      state.employeesList = action.payload;
      state.employeesListStatus = SliceStatus.Fulfilled;
    });
    builder.addCase(doGetDashboardEmployeesList.rejected, (state) => {
      state.employeesListStatus = SliceStatus.Error;
    });
    builder.addCase(doGetDashboardDevicesList.pending, (state) => {
      state.devicesListSliceStatus = SliceStatus.Updating;
    });
    builder.addCase(doGetDashboardDevicesList.fulfilled, (state, action) => {
      if (action.payload.data) {
        state.devicesList = action.payload.data;
      }
      state.devicesListSliceStatus = SliceStatus.Fulfilled;
    });
    builder.addCase(doGetDashboardDevicesList.rejected, (state) => {
      state.devicesListSliceStatus = SliceStatus.Error;
    });
    builder.addCase(doGetDashboardUpcomingBirthdays.pending, (state) => {
      state.upcomingBirthdaysStatus = SliceStatus.Updating;
    });
    builder.addCase(
      doGetDashboardUpcomingBirthdays.fulfilled,
      (state, action) => {
        state.upcomingBirthdays = action.payload;
        state.upcomingBirthdaysStatus = SliceStatus.Fulfilled;
      }
    );
    builder.addCase(doGetDashboardUpcomingBirthdays.rejected, (state) => {
      state.upcomingBirthdaysStatus = SliceStatus.Error;
    });
    builder.addCase(
      doGetDashboardUpcomingSeniorityAnniversaries.pending,
      (state) => {
        state.upcomingSeniorityAnniversariesStatus = SliceStatus.Updating;
      }
    );
    builder.addCase(
      doGetDashboardUpcomingSeniorityAnniversaries.fulfilled,
      (state, action) => {
        state.upcomingSeniorityAnniversaries = action.payload;
        state.upcomingSeniorityAnniversariesStatus = SliceStatus.Fulfilled;
      }
    );
    builder.addCase(
      doGetDashboardUpcomingSeniorityAnniversaries.rejected,
      (state) => {
        state.upcomingSeniorityAnniversariesStatus = SliceStatus.Error;
      }
    );
  },
});

export default dashboardSlice.reducer;
